import { jsPDF } from 'jspdf';
import { formatNumber, formatDate } from './helpers';
import { numberToWordsFr } from './finance';

const hexToRgb = (hex) => {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || '');
  return m ? { r: parseInt(m[1], 16), g: parseInt(m[2], 16), b: parseInt(m[3], 16) } : { r: 30, g: 64, b: 175 };
};

// Génère le PDF d'un devis ou d'une facture (A4, jsPDF, aucune ressource externe)
export const generateFacturePdf = (facture, profile = {}) => {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const W = doc.internal.pageSize.getWidth();
  const margin = 18;
  const brand = hexToRgb(profile.logoColor || '#1E40AF');
  const isDevis = facture.type === 'devis';
  const title = isDevis ? 'DEVIS' : 'FACTURE';
  let y = margin;

  // En-tête
  if (profile.logoUrl && profile.logoUrl.startsWith('data:image')) {
    try { doc.addImage(profile.logoUrl, 'PNG', margin, y, 28, 28); } catch (e) { /* logo ignoré */ }
  }
  doc.setTextColor(brand.r, brand.g, brand.b);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(15);
  doc.text(profile.nomSociete || profile.nomArchitecte || 'Cabinet d\'architecture', margin + 32, y + 8);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(90, 90, 90);
  const headLines = [];
  if (profile.nomArchitecte && profile.nomSociete) headLines.push(profile.nomArchitecte);
  if (profile.siegeSocial) headLines.push(profile.siegeSocial);
  const contact = [profile.mobile, profile.fixe, profile.email].filter(Boolean).join(' · ');
  if (contact) headLines.push(contact);
  headLines.forEach((l, i) => doc.text(l, margin + 32, y + 14 + i * 4.5));

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  doc.setTextColor(brand.r, brand.g, brand.b);
  doc.text(title, W - margin, y + 10, { align: 'right' });
  doc.setFontSize(10);
  doc.setTextColor(60, 60, 60);
  doc.setFont('helvetica', 'normal');
  doc.text(`N° ${facture.numero || '-'}`, W - margin, y + 17, { align: 'right' });
  doc.text(`Date : ${formatDate(facture.date)}`, W - margin, y + 22, { align: 'right' });
  if (facture.refGlobal) doc.text(`Dossier : ${facture.refGlobal}`, W - margin, y + 27, { align: 'right' });

  y += 38;
  doc.setDrawColor(brand.r, brand.g, brand.b);
  doc.setLineWidth(0.6);
  doc.line(margin, y, W - margin, y);
  y += 8;

  // Client
  doc.setFillColor(245, 247, 250);
  doc.roundedRect(margin, y, W - 2 * margin, 22, 2, 2, 'F');
  doc.setFontSize(8);
  doc.setTextColor(120, 120, 120);
  doc.text('CLIENT', margin + 4, y + 6);
  doc.setFontSize(11);
  doc.setTextColor(30, 30, 30);
  doc.setFont('helvetica', 'bold');
  doc.text(facture.clientNom || '-', margin + 4, y + 12);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(90, 90, 90);
  const clientLine2 = [facture.clientIdentifiant, facture.clientAdresse].filter(Boolean).join(' · ');
  if (clientLine2) doc.text(clientLine2, margin + 4, y + 17.5);
  y += 28;

  if (facture.objet) {
    doc.setFontSize(10);
    doc.setTextColor(30, 30, 30);
    doc.setFont('helvetica', 'bold');
    doc.text('Objet : ', margin, y);
    doc.setFont('helvetica', 'normal');
    doc.text(facture.objet, margin + 14, y);
    y += 8;
  }

  // Tableau des lignes
  const colX = { des: margin + 3, qte: W - margin - 62, pu: W - margin - 36, total: W - margin - 3 };
  doc.setFillColor(brand.r, brand.g, brand.b);
  doc.rect(margin, y, W - 2 * margin, 8, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text('Désignation', colX.des, y + 5.5);
  doc.text('Qté', colX.qte, y + 5.5, { align: 'right' });
  doc.text('P.U. HT', colX.pu, y + 5.5, { align: 'right' });
  doc.text('Total HT', colX.total, y + 5.5, { align: 'right' });
  y += 8;
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(40, 40, 40);
  (facture.lignes || []).forEach((l, i) => {
    const qte = parseFloat(l.quantite) || 0;
    const pu = parseFloat(l.prixUnitaire) || 0;
    const lines = doc.splitTextToSize(l.designation || '-', colX.qte - colX.des - 12);
    const h = Math.max(8, lines.length * 4.5 + 3);
    if (y + h > doc.internal.pageSize.getHeight() - 60) { doc.addPage(); y = margin; }
    if (i % 2 === 1) { doc.setFillColor(248, 249, 251); doc.rect(margin, y, W - 2 * margin, h, 'F'); }
    doc.text(lines, colX.des, y + 5);
    doc.text(String(qte), colX.qte, y + 5, { align: 'right' });
    doc.text(formatNumber(pu), colX.pu, y + 5, { align: 'right' });
    doc.text(formatNumber(qte * pu), colX.total, y + 5, { align: 'right' });
    y += h;
  });
  doc.setDrawColor(220, 220, 220);
  doc.line(margin, y, W - margin, y);
  y += 6;

  // Totaux
  const totals = [['Total HT', facture.montantHT], [`TVA ${facture.tauxTVA || 0} %`, facture.montantTVA]];
  if (facture.fraisTimbre) totals.push(['Total TTC', facture.montantTTC], ['Frais du timbre (0,25 %)', facture.fraisTimbre]);
  doc.setFontSize(10);
  totals.forEach(([label, val]) => {
    doc.setTextColor(90, 90, 90);
    doc.text(label, W - margin - 50, y, { align: 'right' });
    doc.setTextColor(30, 30, 30);
    doc.text(`${formatNumber(val || 0)} DH`, W - margin - 3, y, { align: 'right' });
    y += 6;
  });
  doc.setFillColor(brand.r, brand.g, brand.b);
  doc.roundedRect(W - margin - 80, y - 4, 80, 10, 1.5, 1.5, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.text(facture.fraisTimbre ? 'Total à payer' : 'Total TTC', W - margin - 50, y + 2.5, { align: 'right' });
  doc.text(`${formatNumber(facture.totalFinal || facture.montantTTC || 0)} DH`, W - margin - 3, y + 2.5, { align: 'right' });
  y += 14;

  doc.setFont('helvetica', 'italic');
  doc.setFontSize(9);
  doc.setTextColor(80, 80, 80);
  const words = `Arrêté ${isDevis ? 'le présent devis' : 'la présente facture'} à la somme de : ${numberToWordsFr(Math.floor(facture.totalFinal || facture.montantTTC || 0))} dirhams TTC.`;
  doc.text(doc.splitTextToSize(words, W - 2 * margin), margin, y);
  y += 12;

  if (facture.notes) {
    doc.setFont('helvetica', 'normal');
    doc.text(doc.splitTextToSize(facture.notes, W - 2 * margin), margin, y);
    y += 10;
  }

  // Pied : identifiants fiscaux + banque + signature
  const H = doc.internal.pageSize.getHeight();
  const fiscal = [];
  if (profile.ice) fiscal.push(`ICE ${profile.ice}`);
  if (profile.identifiantFiscal) fiscal.push(`IF ${profile.identifiantFiscal}`);
  if (profile.rc) fiscal.push(`RC ${profile.rc}`);
  if (profile.patente) fiscal.push(`Patente ${profile.patente}`);
  const bank = [profile.banque, profile.rib ? `RIB ${profile.rib}` : ''].filter(Boolean).join(' · ');
  doc.setFontSize(9);
  doc.setTextColor(50, 50, 50);
  doc.text('Signature et cachet', W - margin - 55, H - 48);
  doc.setDrawColor(200, 200, 200);
  doc.rect(W - margin - 60, H - 45, 60, 25);
  doc.setFontSize(8);
  doc.setTextColor(120, 120, 120);
  if (fiscal.length) doc.text(fiscal.join(' · '), W / 2, H - 14, { align: 'center' });
  if (bank) doc.text(bank, W / 2, H - 10, { align: 'center' });

  doc.save(`${title}_${(facture.numero || 'brouillon').replace('/', '-')}_${(facture.clientNom || 'client').replace(/[^\w-]+/g, '_')}.pdf`);
};
